// BLSMobileGate - tenant-themed "view on desktop" screen for BLS demo
// subdomains when opened on mobile.
//
// The BLS agent experience (Want-Listing dashboard, demand insights,
// match feed, intro tool) is built for desktop workflow. Mobile users
// hitting *.thebuyerlistingservice.com see this gate instead of being
// pushed into the mobile-app routes that were designed for the consumer
// Gitcha product.
//
// Renders only when both checks are true (see helper exports below).
// Consumer Gitcha (gitcha.com) is unaffected.
import { useEffect, useState } from "react";
import { useAppConfig } from "../../appConfig";

// Hostname matches *.thebuyerlistingservice.com (with or without subdomain),
// or the local-dev variants. Returns false for gitcha.com / localhost so
// the consumer mobile app keeps working untouched.
export const isBLSDomain = () => {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  return (
    host.endsWith(".thebuyerlistingservice.com") ||
    host === "thebuyerlistingservice.com" ||
    host.endsWith(".local.thebuyerlistingservice.com") ||
    host === "local.thebuyerlistingservice.com"
  );
};

// Same userAgent regex App.js uses elsewhere - kept consistent so the
// gate fires on the same devices the existing mobile-routing code does.
export const isMobileBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || ""
  );
};

const MOBILE_BREAKPOINT_PX = 991;
const PREVIEW_STORAGE_KEY = "blsMobilePreview";

export const isSmallViewport = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= MOBILE_BREAKPOINT_PX;
};

export const hasMobilePreviewFlag = () => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const param = params.get("mobilePreview");
    if (param === "1") {
      window.localStorage.setItem(PREVIEW_STORAGE_KEY, "1");
      return true;
    }
    if (param === "0") {
      window.localStorage.removeItem(PREVIEW_STORAGE_KEY);
      return false;
    }
    return window.localStorage.getItem(PREVIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const shouldShowBLSGate = () => {
  if (!isBLSDomain()) return false;
  if (hasMobilePreviewFlag()) return false;
  return isMobileBrowser() || isSmallViewport();
};

const SPLASH_GRADIENT_DEFAULT =
  "linear-gradient(135deg, #060d1a 0%, #0a1628 50%, #0f1f3a 100%)";

const BLSMobileGate = () => {
  const config = useAppConfig();
  const tenant = config?.tenant;
  const logoSrc =
    tenant?.assets?.logos?.logoWhite || tenant?.assets?.logos?.logoV1;
  const tenantName = tenant?.head?.title || "The Buyer Listing Service";
  const splashGradient =
    tenant?.themeVars?.["--splash-gradient"] || SPLASH_GRADIENT_DEFAULT;

  // Strip trailing site name so the page heading just shows the
  // brand chunk (e.g. "LAR" not "LAR - Buyer Listing Service").
  const tenantBrand = tenantName.replace(/\s*[-|]\s*.*$/, "");

  const desktopUrl =
    typeof window !== "undefined" ? window.location.origin : "";

  return (
    <section
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        background: splashGradient,
        color: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 24px",
        textAlign: "center",
        fontFamily: "'Supreme-Regular', system-ui, -apple-system, sans-serif",
        overflow: "auto",
      }}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={tenantBrand}
          style={{
            height: 38,
            width: "auto",
            marginBottom: 36,
            filter: "brightness(1.1)",
          }}
        />
      ) : null}

      <div
        aria-hidden="true"
        style={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.08)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 28,
        }}
      >
        <svg
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "#ffffff", opacity: 0.9 }}
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8" />
          <path d="M12 17v4" />
        </svg>
      </div>

      <h1
        style={{
          fontFamily: "'Supreme-Bold', system-ui, sans-serif",
          fontSize: 28,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          margin: "0 0 16px 0",
          maxWidth: 480,
        }}
      >
        Built for Desktop
      </h1>

      <p
        style={{
          fontSize: 16,
          lineHeight: 1.55,
          opacity: 0.78,
          margin: "0 0 28px 0",
          maxWidth: 460,
        }}
      >
        The Buyer Listing Service is designed for the desktop workflow agents
        use to broadcast buyer demand, see real-time match insights, and run
        listing appointments. Please open this experience on a laptop or
        desktop browser.
      </p>

      <div
        style={{
          background: "rgba(255, 255, 255, 0.06)",
          border: "1px solid rgba(255, 255, 255, 0.14)",
          borderRadius: 8,
          padding: "12px 16px",
          fontSize: 13,
          fontFamily:
            "'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace",
          color: "rgba(255, 255, 255, 0.92)",
          maxWidth: "100%",
          wordBreak: "break-all",
        }}
      >
        {desktopUrl}
      </div>

      <p
        style={{
          marginTop: 36,
          fontSize: 13,
          opacity: 0.55,
          maxWidth: 380,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {tenantBrand}
      </p>
    </section>
  );
};

export const BLSGateWrapper = ({ children }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const handler = () => setTick((t) => t + 1);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  if (shouldShowBLSGate()) return <BLSMobileGate />;
  return children;
};

export default BLSMobileGate;
